import {useSelector} from 'react-redux';

import {serverAPI} from 'features/server/api/serverAPI';
import {
    getServersAlertRulesHintVariant,
    ServersAlertRulesHintVariant,
} from 'features/server/lib/serverAlertRuleHelper';
import {getServerFilterArray} from 'features/server/lib/serverFilters';
import {useMonitorList, IMonitorListState} from 'entities/monitor';
import {userSelector, useHasTariffLimit} from 'entities/user';
import IPagination from 'shared/api/model/IPagination';
import {getPollingInterval} from 'shared/api/pollingHelper';
import useListParams from 'shared/lib/hooks/useListParams';
import IOrderBy from 'shared/model/IOrderBy';
import {SetOrderByType} from 'shared/ui/Table/Table';

import IServerOutput from './Server/IServer';
import {clearAll, isSelectedAllSelector, selectAllOnPage, serverSelectedIdsSelector} from './serverSlice';
import useCanCreateServer from './useCanCreateServer';
import useCanCreateServerAlertRule from './useCanCreateServerAlertRule';

export interface IUseServers {
    pagination: IPagination<IServerOutput> | undefined,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
    search: string,
    onSearch: (_search: string) => void,
    sort: IOrderBy | undefined,
    setSort: SetOrderByType,
    serversCount: number | undefined,
    isEmpty: boolean,
    hasServers: boolean,
    list: IMonitorListState,
    maxServers: number | undefined,
    alertRulesLimit: number,
    hintVariant: ServersAlertRulesHintVariant | null,
    canCreate: boolean,
    canCreateAlertRule: boolean,
}

export const useServers = (): IUseServers => {
    const {args, sort, setSort, filters, hasFilters, search, onSearch} = useListParams({
        getFilters: getServerFilterArray,
    });

    const user = useSelector(userSelector);
    const hasAccess = useHasTariffLimit('server_max_count_limit');

    const {data: pagination, isLoading, isFetching, refetch} = serverAPI.useGetServersQuery(
        args,
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );
    const {data: allPagination} = serverAPI.useGetServersQuery({}, {skip: !hasAccess || !hasFilters});

    const [getAllServerIds] = serverAPI.useLazyGetAllServerIdsQuery();

    const list = useMonitorList({
        pagination,
        isLoading,
        tariffField: 'server_max_count_limit',
        hasFilters,
        selection: {
            selectedIdsSelector: serverSelectedIdsSelector,
            isSelectedAllSelector,
            selectAllOnPage,
            clearAll,
        },
        getAllIds: async() => (await getAllServerIds({sort, filters})).data,
    });
    const {plugState, isRedirecting} = list;

    const serversCount = hasFilters ? allPagination?.totalItems : pagination?.totalItems;
    const isLoaded = hasAccess && !isLoading;
    const isEmpty = plugState === 'empty';
    const hasServers = isLoaded && !!serversCount;

    const alertRulesLimit = user?.tariff?.server_alert_rules_max_count_limit ?? 0;
    const alertRulesCount = user?.server_alert_rules_count ?? 0;

    return {
        pagination,
        isLoading: isLoading || isRedirecting,
        isFetching,
        refetch,
        search,
        onSearch,
        sort,
        setSort,
        serversCount,
        isEmpty,
        hasServers,
        list,
        maxServers: user?.tariff?.server_max_count_limit,
        alertRulesLimit,
        hintVariant: getServersAlertRulesHintVariant(hasServers, alertRulesCount, alertRulesLimit),
        canCreate: useCanCreateServer({serversCount: serversCount ?? 0}),
        canCreateAlertRule: useCanCreateServerAlertRule(),
    };
};

export default useServers;
